import { Space as AntSpace } from 'antd';
import { isUndefined } from 'lodash';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { CustomDomainHost } from 'api/account';
import { LinkButton } from 'components/atoms/button';
import { CopyToClipboard } from 'components/atoms/copy-to-clipboard';
import { useTranslation } from 'hooks/use-translation';
import { SettingsLabelWithoutMargin } from '../../styles';
import { Space, TitleHelper } from '../custom-domain.styles';
import { Cell, Table } from './domain-records.styles';

interface PartialCustomDomainHost extends Partial<CustomDomainHost> {
  host: string | undefined;
  value: string | undefined;
}

const dummyData: PartialCustomDomainHost = {
  cnameType: 'SSL',
  host: undefined,
  value: undefined,
};

const getColumns = (refetch: () => void, isFetching = false) => [
  {
    title: <FormattedMessage id="records" />,
    dataIndex: 'cnameType',
    width: 100,
    render() {
      return 'CNAME';
    },
  },
  {
    title: <FormattedMessage id="key" />,
    dataIndex: 'host',
    width: 350,
    render: (key: string) => (
      <Cell>
        {isUndefined(key) ? (
          <LinkButton
            className="reload-button"
            onClick={refetch}
            disabled={isFetching}
          >
            <FormattedMessage
              id={isFetching ? 'loading' : 'settings.custom_domain.cell.reload'}
            />
          </LinkButton>
        ) : (
          <>
            <p>{key}</p>
            <CopyToClipboard textToCopy={key} />
          </>
        )}
      </Cell>
    ),
  },
  {
    title: <FormattedMessage id="value" />,
    dataIndex: 'value',
    width: 350,
    render: (value: string) => (
      <Cell>
        {isUndefined(value) ? (
          <LinkButton
            className="reload-button"
            onClick={refetch}
            disabled={isFetching}
          >
            <FormattedMessage
              id={isFetching ? 'loading' : 'settings.custom_domain.cell.reload'}
            />
          </LinkButton>
        ) : (
          <>
            <p>{value}</p>
            <CopyToClipboard textToCopy={value} />
          </>
        )}
      </Cell>
    ),
  },
];

export const DomainRecords: FC<{
  data: CustomDomainHost[];
  isFetching?: boolean;
  refetch: () => void;
}> = ({ data = [], isFetching = false, refetch }) => {
  const title = useTranslation('settings.custom_domain.step.title', {
    current: 1,
    total: 2,
  });
  const description = useTranslation(
    'settings.custom_domain.step.one.description',
  );

  const tableData =
    data.length < 4
      ? [...data, ...new Array(4 - data.length).fill(dummyData)]
      : data;

  return (
    <Space size="large">
      <AntSpace>
        <SettingsLabelWithoutMargin>{title}:</SettingsLabelWithoutMargin>
        <TitleHelper>{description}</TitleHelper>
      </AntSpace>
      <Table
        tableLayout="fixed"
        columns={getColumns(refetch, isFetching)}
        dataSource={tableData}
        pagination={false}
      />
    </Space>
  );
};
